import { useEffect, useMemo, useState } from 'react';
import { EditFlightPayload, Flight } from '../../types';
import { useFeedback } from '../../hooks/useFeedback';
import { useSubmitGuard } from '../../hooks/useSubmitGuard';
import { formatHours } from '../../utils/flightDisplay';
import {
  ENGINE_RUN_TYPE,
  FLIGHT_TYPES,
  formatRefuelLine,
  parseFlightNumber,
  parseLandings,
  refuelsPayload,
  toRefuelDrafts,
  type FlightRefuelDraft,
} from '../../utils/flightForm';
import AddRefuelModal from '../AddRefuelModal/AddRefuelModal';
import IssueFilePicker from '../IssueFilePicker/IssueFilePicker';
import ModalPortal from '../modal/ModalPortal';
import { TACH_FILE_ACCEPT, TACH_FILE_HINT } from '../../utils/issueFiles';
import { deleteFlightAttachment, deleteIssueAttachment, getUsers } from '../../api';
import { FlightAttachment, IssueAttachment } from '../../types';
import { getApiErrorMessage } from '../../utils/apiError';
import { isGeneralUser } from '../../utils/isGeneralUser';
import { fuelSplitError, refuelPurchaseTotal } from '../../utils/expenseSplit';
import '../IssueFilePicker/IssueFilePicker.css';
import './EditFlightModal.css';

interface Props {
  flight: Flight;
  pilots: { id: number; name: string }[];
  defaultFuelLocation?: string;
  allowFullSplit?: boolean;
  onClose: () => void;
  onSave: (
    data: EditFlightPayload,
    extras: {
      tachFiles: File[];
      issueId: number | null;
      issueDescription: string;
      issueSeverity: string;
      issueFiles: File[];
    }
  ) => void | Promise<void>;
}

const EditFlightModal = ({
  flight,
  pilots,
  defaultFuelLocation = '',
  allowFullSplit = false,
  onClose,
  onSave,
}: Props) => {
  const { showError, feedbackNode } = useFeedback();
  const { submitting, runSubmit } = useSubmitGuard();
  const fallbackLocation =
    defaultFuelLocation ||
    flight.refuels?.[0]?.location ||
    flight.fuel_location ||
    '';
  const firstIssue = flight.issues?.[0] || null;
  const [form, setForm] = useState({
    pilot_id: flight.pilot_id || '',
    flight_date: String(flight.date).slice(0, 10),
    hobbs_end: formatHours(Number(flight.hobbs_end)),
    hours: formatHours(Number(flight.hours)),
    flight_type: flight.flight_type || 'По кругам',
    landings: String(
      flight.flight_type === ENGINE_RUN_TYPE ? 0 : flight.landings ?? 1
    ),
    oil: flight.oil != null ? String(flight.oil) : '',
    comment: flight.comment || '',
    issue_description: firstIssue?.description || flight.issue || '',
    issue_severity: firstIssue?.severity || '',
  });
  const [showOil, setShowOil] = useState(flight.oil != null);
  const [showComment, setShowComment] = useState(Boolean(flight.comment));
  const [refuels, setRefuels] = useState<FlightRefuelDraft[]>(() =>
    toRefuelDrafts(flight.refuels, fallbackLocation, flight.pilot_id)
  );
  const [refuelEditor, setRefuelEditor] = useState<{
    index: number | null;
  } | null>(null);
  const [payerOptions, setPayerOptions] = useState<{ id: number; name: string }[]>(
    () => pilots
  );
  const [tachFiles, setTachFiles] = useState<File[]>([]);
  const [issueFiles, setIssueFiles] = useState<File[]>([]);
  const [storedTach, setStoredTach] = useState<FlightAttachment[]>(
    () => flight.attachments || []
  );
  const [storedIssueFiles, setStoredIssueFiles] = useState<IssueAttachment[]>(
    () => firstIssue?.attachments || []
  );
  const isEngineRun = form.flight_type === ENGINE_RUN_TYPE;

  useEffect(() => {
    let cancelled = false;
    void getUsers()
      .then((res) => {
        if (cancelled) return;
        setPayerOptions(
          (res.data || [])
            .filter((user) => !isGeneralUser(user))
            .map((user) => ({ id: user.id, name: user.name }))
        );
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const landingsValue = useMemo(
    () => (isEngineRun ? '0' : form.landings),
    [isEngineRun, form.landings]
  );

  const handleTypeChange = (flight_type: string) => {
    setForm((prev) => ({
      ...prev,
      flight_type,
      landings:
        flight_type === ENGINE_RUN_TYPE
          ? '0'
          : prev.flight_type === ENGINE_RUN_TYPE && prev.landings === '0'
            ? '1'
            : prev.landings,
    }));
  };

  const handleSave = () => {
    if (submitting) return;
    if (!form.pilot_id) {
      showError('Выберите пилота!');
      return;
    }
    const parsedHobbs = parseFlightNumber(form.hobbs_end);
    if (parsedHobbs === null || !Number.isFinite(parsedHobbs)) {
      showError('Введите число в показании тахометра');
      return;
    }
    if (parsedHobbs <= 0) {
      showError('Тахометр должен быть больше 0');
      return;
    }
    const parsedHours = parseFlightNumber(form.hours);
    if (parsedHours === null || !Number.isFinite(parsedHours)) {
      showError('Введите число в моточасах');
      return;
    }
    if (parsedHours < 0) {
      showError('Моточасы не могут быть отрицательными');
      return;
    }
    if (form.issue_description.trim() && !form.issue_severity && !firstIssue) {
      showError('Укажите степень важности неисправности');
      return;
    }
    for (const item of refuels) {
      const splitError = fuelSplitError(
        {
          costSplit: item.costSplit,
          manualShares: item.manualShares,
          flightIds: item.flightIds,
          payerUserId: item.payerUserId || Number(form.pilot_id) || 0,
        },
        refuelPurchaseTotal([item]) > 0
      );
      if (splitError) {
        showError(splitError);
        return;
      }
    }
    void runSubmit(() =>
      onSave(
        {
          pilot_id: Number(form.pilot_id),
          flight_date: form.flight_date,
          hobbs_end: parsedHobbs,
          hours: parsedHours,
          flight_type: form.flight_type,
          landings: parseLandings(landingsValue, form.flight_type),
          oil: showOil && form.oil !== '' ? Number(form.oil) : null,
          comment: form.comment.trim(),
          refuels: refuelsPayload(refuels),
        },
        {
          tachFiles,
          issueId: firstIssue?.id ?? null,
          issueDescription: form.issue_description.trim(),
          issueSeverity: form.issue_severity || firstIssue?.severity || 'medium',
          issueFiles,
        }
      )
    );
  };

  const removeStoredTach = async (file: FlightAttachment) => {
    if (!window.confirm(`Удалить «${file.original_name || file.filename}»?`)) {
      return;
    }
    try {
      await deleteFlightAttachment(file.flight_id, file.id);
      setStoredTach((current) => current.filter((item) => item.id !== file.id));
    } catch (error) {
      showError(getApiErrorMessage(error, 'Не удалось удалить файл'));
    }
  };

  const removeStoredIssueFile = async (file: IssueAttachment) => {
    if (!window.confirm(`Удалить «${file.original_name || file.filename}»?`)) {
      return;
    }
    try {
      await deleteIssueAttachment(file.issue_id, file.id);
      setStoredIssueFiles((current) =>
        current.filter((item) => item.id !== file.id)
      );
    } catch (error) {
      showError(getApiErrorMessage(error, 'Не удалось удалить файл'));
    }
  };

  return (
    <ModalPortal>
    <div className="modal-overlay">
      {feedbackNode}
      <div className="modal edit-flight-modal">
        <h3>Редактировать полёт #{flight.id}</h3>

        <div className="edit-flight-form">
          <h4 className="form-section-title">Информация о полёте</h4>

          <div className="field-group">
            <label htmlFor="edit-flight-pilot">Пилот</label>
            <select
              id="edit-flight-pilot"
              value={form.pilot_id}
              onChange={(e) => setForm({ ...form, pilot_id: Number(e.target.value) })}
            >
              <option value="">Выберите пилота</option>
              {pilots.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <div className="field-group">
            <label htmlFor="edit-flight-date">Дата</label>
            <input
              id="edit-flight-date"
              type="date"
              value={form.flight_date}
              onChange={(e) => setForm({ ...form, flight_date: e.target.value })}
            />
          </div>

          <p className="form-hint">
            Моточасы — налёт этого рейса (двигают ТО). Тахометр — снимок прибора,
            на другие рейсы не влияет.
          </p>

          <div className="field-group">
            <label htmlFor="edit-flight-hobbs">Тахометр</label>
            <input
              id="edit-flight-hobbs"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={form.hobbs_end}
              onChange={(e) =>
                setForm({
                  ...form,
                  hobbs_end: e.target.value,
                })
              }
            />
          </div>

          <div className="field-group">
            <label htmlFor="edit-flight-hours">Моточасы</label>
            <input
              id="edit-flight-hours"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={form.hours}
              onChange={(e) =>
                setForm({
                  ...form,
                  hours: e.target.value,
                })
              }
            />
          </div>

          <div className="field-group form-span">
            <IssueFilePicker
              files={tachFiles}
              onChange={setTachFiles}
              label="Фото тахометра"
              accept={TACH_FILE_ACCEPT}
              hint={TACH_FILE_HINT}
            />
            {storedTach.length > 0 ? (
              <ul className="issue-file-pending">
                {storedTach.map((file) => (
                  <li key={file.id}>
                    <span>{file.original_name || file.filename}</span>
                    <button
                      type="button"
                      className="issue-file-remove"
                      onClick={() => void removeStoredTach(file)}
                    >
                      Удалить
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          <div className="field-group">
            <label htmlFor="edit-flight-type">Тип полёта</label>
            <select
              id="edit-flight-type"
              value={form.flight_type}
              onChange={(e) => handleTypeChange(e.target.value)}
            >
              {FLIGHT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div className="field-group">
            <label htmlFor="edit-flight-landings">Посадки</label>
            <input
              id="edit-flight-landings"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={landingsValue}
              disabled={isEngineRun}
              onChange={(e) => setForm({ ...form, landings: e.target.value })}
            />
          </div>

          <h4 className="form-section-title">Заправка</h4>
          {refuels.map((item, index) => (
            <div key={item.key} className="fuel-row">
              <button
                type="button"
                className="fuel-row-main"
                onClick={() => setRefuelEditor({ index })}
              >
                {formatRefuelLine(item)}
              </button>
              <button
                type="button"
                className="fuel-row-remove"
              onClick={() =>
                setRefuels((current) =>
                  current.filter((_, itemIndex) => itemIndex !== index)
                )
              }
              >
                Убрать
              </button>
            </div>
          ))}
          <button
            type="button"
            className="form-add-link"
            onClick={() => setRefuelEditor({ index: null })}
          >
            {refuels.length > 0
              ? '+ добавить ещё заправку топливом'
              : '+ добавить заправку топливом'}
          </button>
          {refuels.length > 0 ? (
            <p className="form-hint">
              Распределение можно поменять, открыв строку заправки.
            </p>
          ) : null}
          {showOil ? (
            <div className="fuel-row">
              <div className="field-group field-oil">
                <label htmlFor="edit-flight-oil">Доливка масла, л</label>
                <input
                  id="edit-flight-oil"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  value={form.oil}
                  onChange={(e) => setForm({ ...form, oil: e.target.value })}
                />
              </div>
              <button
                type="button"
                className="fuel-row-remove"
                onClick={() => {
                  setShowOil(false);
                  setForm((prev) => ({ ...prev, oil: '' }));
                }}
              >
                Убрать
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="form-add-link"
              onClick={() => setShowOil(true)}
            >
              + добавить доливку масла в двигатель
            </button>
          )}

          <h4 className="form-section-title">Добавить неисправность</h4>
          <div className="field-group">
            <label htmlFor="edit-flight-issue">Неисправность</label>
            <input
              id="edit-flight-issue"
              type="text"
              placeholder="Неисправность"
              value={form.issue_description}
              onChange={(e) =>
                setForm({ ...form, issue_description: e.target.value })
              }
            />
          </div>
          <div className="field-group">
            <label htmlFor="edit-flight-severity">Степень важности</label>
            <select
              id="edit-flight-severity"
              value={form.issue_severity}
              onChange={(e) =>
                setForm({ ...form, issue_severity: e.target.value })
              }
            >
              <option value="" disabled>
                Степень важности
              </option>
              <option value="low">Низкая</option>
              <option value="medium">Средняя</option>
              <option value="high">Высокая</option>
            </select>
          </div>
          <div className="field-group form-span">
            <IssueFilePicker
              files={issueFiles}
              onChange={setIssueFiles}
            />
            {storedIssueFiles.length > 0 ? (
              <ul className="issue-file-pending">
                {storedIssueFiles.map((file) => (
                  <li key={file.id}>
                    <span>{file.original_name || file.filename}</span>
                    <button
                      type="button"
                      className="issue-file-remove"
                      onClick={() => void removeStoredIssueFile(file)}
                    >
                      Удалить
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          {showComment || form.comment ? (
            <div className="field-group form-span">
              <label htmlFor="edit-flight-comment">Комментарий / заметка</label>
              <textarea
                id="edit-flight-comment"
                value={form.comment}
                onChange={(e) => setForm({ ...form, comment: e.target.value })}
                placeholder="Любая информация по этому полёту..."
                rows={4}
              />
            </div>
          ) : (
            <button
              type="button"
              className="form-add-link"
              onClick={() => setShowComment(true)}
            >
              + Добавить комментарий/заметку
            </button>
          )}
        </div>

        <div className="modal-actions">
          <button
            type="button"
            className="btn-cancel"
            onClick={onClose}
            disabled={submitting}
          >
            Отмена
          </button>
          <button
            type="button"
            className="btn-save"
            onClick={handleSave}
            disabled={submitting}
          >
            {submitting ? '…' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
    {refuelEditor ? (
      <AddRefuelModal
        defaultLocation={fallbackLocation}
        initial={
          refuelEditor.index != null ? refuels[refuelEditor.index] : null
        }
        date={form.flight_date}
        defaultPayerId={Number(form.pilot_id) || 0}
        payerOptions={payerOptions}
        allowFullSplit={allowFullSplit}
        onClose={() => setRefuelEditor(null)}
        onSave={(item) => {
          setRefuels((current) => {
            if (refuelEditor.index == null) {
              return [
                ...current,
                { ...item, key: `${Date.now()}-${current.length}` },
              ];
            }
            return current.map((row, index) =>
              index === refuelEditor.index ? { ...row, ...item } : row
            );
          });
          setRefuelEditor(null);
        }}
      />
    ) : null}
    </ModalPortal>
  );
};

export default EditFlightModal;
